import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import {
  useRegisterSessionMutation,
  useHeartbeatSessionMutation,
  useRevokeSessionMutation,
  useRevokeAllSessionsMutation,
  useGetAllSessionsQuery,
} from '@src/Api/TraceApi/traceApi';
import {
  IDeviceInfo,
  ISession,
} from '@src/Api/TraceApi/endpoints/sessions.endpoint';
import useFireBaseAuthentication from '@src/Hooks/useFirebaseAuthentication';
import {useSessionId, SESSION_ID_KEY} from './useSessionId';
import useEvent from '@src/Hooks/useEvent';
import {auth} from '@src/firebase';

// Define API error interface
interface ApiError {
  status?: number;
  data?: any;
  message?: string;
}

interface SessionContextType {
  sessionId: string | null;
  userSessions: ISession[];
  isLoading: boolean;
  maxSessions: number;
  maxSessionsReached: boolean;
  isSessionAlertOpen: boolean;
  createNewSession: () => Promise<string | null>;
  revokeSession: (sessionId: string, isLogout?: boolean) => Promise<boolean>;
  revokeOtherSessions: () => Promise<boolean>;
  refreshSessions: () => Promise<void>;
  initializeSession: () => Promise<boolean>;
  handleExpiredSession: (id?: string) => Promise<void>;
  heartBeat: () => Promise<void>;
  setIsSessionAlertOpen: (isOpen: boolean) => void;
}

export const SessionContext = createContext<SessionContextType>({
  sessionId: null,
  userSessions: [],
  isLoading: true,
  maxSessions: 3,
  maxSessionsReached: false,
  isSessionAlertOpen: false,
  createNewSession: async () => null,
  revokeSession: async () => false,
  revokeOtherSessions: async () => false,
  refreshSessions: async () => {},
  initializeSession: async () => false,
  handleExpiredSession: async () => {},
  heartBeat: async () => {},
  setIsSessionAlertOpen: () => {},
});

export const useSession = () => useContext(SessionContext);

interface SessionProviderProps {
  children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({children}) => {
  const {signOut, isConnected} = useFireBaseAuthentication();
  const [sessionId, setSessionId, clearSessionId] = useSessionId();
  const [isSessionAlertOpen, setIsSessionAlertOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [maxSessionsReached, setMaxSessionsReached] = useState(false);
  const maxSessions = 3; // Maximum sessions allowed per user

  // Flag to store active initializeSession promise
  const activeInitPromiseRef = useRef<Promise<boolean> | null>(null);
  const activeCreateSessionPromiseRef = useRef<Promise<string | null> | null>(
    null,
  );
  // Ref to track heartbeat requests for aborting
  const heartbeatAbortControllerRef = useRef<{abort: () => void} | null>(null);

  // RTK Query hooks
  const [registerSession] = useRegisterSessionMutation();
  const {
    data: userSessions = [],
    isLoading: isLoadingSessions,
    refetch: refreshSessions,
  } = useGetAllSessionsQuery(undefined, {
    skip: !sessionId,
  });
  const [heartbeatSession] = useHeartbeatSessionMutation();
  const [revokeSessionMutation] = useRevokeSessionMutation();
  const [revokeAllSessionsMutation] = useRevokeAllSessionsMutation();

  const logout = useEvent(async () => {
    await signOut();
    cleanupSession();
  });

  // Add an effect to refresh sessions when sessionId changes
  useEffect(() => {
    if (sessionId) {
      refreshSessions();
    }
  }, [sessionId]);

  // Clean up session data
  const cleanupSession = useEvent(async () => {
    clearSessionId();
    setSessionId(null);
    setMaxSessionsReached(false);
  });

  // Handle expired session
  const handleExpiredSession = useEvent(async (id?: string) => {
    await revokeSession(id || sessionId || '', true);
    await logout();
    await cleanupSession();
    localStorage.removeItem(SESSION_ID_KEY);
  });

  // Heartbeat to keep session active
  const heartBeat = useEvent(async () => {
    const storedSessionId = sessionId;
    const hasFirebaseUser = await isConnected();
    try {
      if (storedSessionId && hasFirebaseUser) {
        // Abort any pending heartbeat request
        if (heartbeatAbortControllerRef.current) {
          heartbeatAbortControllerRef.current.abort();
          heartbeatAbortControllerRef.current = null;
        }

        // Create a new heartbeat request
        const heartbeatRequest = heartbeatSession();

        // Store the abort function for potential cancellation
        heartbeatAbortControllerRef.current = {
          abort: () => heartbeatRequest.abort(),
        };

        await heartbeatRequest.unwrap();
        console.log('Heartbeat sent successfully');

        // Reset the heartbeat timer when a heartbeat is completed successfully
        resetHeartbeatTimer();
      }
    } catch (error) {
      console.error('Error sending heartbeat:', error);
      // Don't show error for aborted requests
      if (
        (error as Error).name === 'AbortError' ||
        (error as any)?.error?.status === 'ABORT_ERROR'
      ) {
        return;
      }
      if ((error as ApiError)?.status === 401) {
        await handleExpiredSession(storedSessionId || '');
      }
    }
  });

  // Reference to store the heartbeat timeout
  const heartbeatTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Function to reset the heartbeat timer
  const resetHeartbeatTimer = useEvent(() => {
    // Clear any existing timeout
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }

    // Set a new timeout for 15 minutes
    heartbeatTimeoutRef.current = setTimeout(
      () => {
        heartBeat();
      },
      15 * 60 * 1000,
    );
  });

  // Setup the initial heartbeat timer and clean it up on unmount
  useEffect(() => {
    // Only set up the timer if we have a sessionId
    if (sessionId) {
      resetHeartbeatTimer();
    }

    return () => {
      // Clean up the timeout when component unmounts
      if (heartbeatTimeoutRef.current) {
        clearTimeout(heartbeatTimeoutRef.current);
        heartbeatTimeoutRef.current = null;
      }
    };
  }, [sessionId]);

  // Initialize or validate existing session
  const initializeSession = useEvent(async (): Promise<boolean> => {
    // If already initializing, return the existing promise to prevent multiple initializations
    if (activeInitPromiseRef.current) {
      console.log(
        'Session initialization already in progress, reusing existing promise',
      );
      return activeInitPromiseRef.current;
    }

    // Create a new initialization promise
    setIsLoading(true);

    const initPromise = (async () => {
      try {
        const existingSessionId = sessionId;
        console.log(
          '🚀 ~ initializeSession ~ existingSessionId:',
          existingSessionId,
          'localStorage:',
          localStorage.getItem(SESSION_ID_KEY),
        );

        // Check localStorage first to ensure we have the most up-to-date sessionId
        const storedSessionId = localStorage.getItem(SESSION_ID_KEY);
        if (storedSessionId && storedSessionId !== existingSessionId) {
          console.log(
            'Found different sessionId in localStorage, updating state',
          );
          setSessionId(storedSessionId);
          // Continue with the stored session id
        }

        // Use the most recent sessionId
        const currentSessionId = storedSessionId;

        if (currentSessionId) {
          // Try to validate the existing session with a heartbeat
          try {
            console.log('🚀 ~ initializeSession ~ heartBeat:');
            await heartBeat();
            console.log('-- Existing session validated with heartbeat');
            return true;
          } catch (error) {
            console.log('-- Error validating existing session:', error);
            const status = (error as ApiError)?.status;
            if (status === 401) {
              console.log('-- Session expired, handling expired session');
              await handleExpiredSession(currentSessionId);
              return false;
            }

            console.error('-- Session invalid, creating new session...', error);
            try {
              const newSession = await createNewSession();
              return !!newSession;
            } catch (error) {
              console.error('-- Error creating new session:', error);
              return false;
            }
          }
        } else {
          // No existing session - create a new one
          console.log('No existing session, creating a new one');
          const newSession = await createNewSession();
          return !!newSession;
        }
      } catch (error) {
        console.error('Error initializing session:', error);
      } finally {
        setIsLoading(false);
        activeInitPromiseRef.current = null;
      }
      return false;
    })();

    // Store the promise for reuse
    activeInitPromiseRef.current = initPromise;

    return initPromise;
  });

  // Create a new session
  const createNewSession = useEvent(async (): Promise<string | null> => {
    // If already creating a session, return the existing promise
    if (activeCreateSessionPromiseRef.current) {
      console.log(
        'Session creation already in progress, reusing existing promise',
      );
      return activeCreateSessionPromiseRef.current;
    }

    const createPromise = (async () => {
      try {
        // Check if there's already a sessionId in localStorage that we might have missed
        const storedSessionId = localStorage.getItem(SESSION_ID_KEY);
        if (storedSessionId && !sessionId) {
          console.log(
            'Found existing session ID in localStorage:',
            storedSessionId,
          );
          setSessionId(storedSessionId);
          return storedSessionId;
        }

        // Collect device information
        const deviceInfo: IDeviceInfo = {
          name: navigator.userAgent,
          platform: navigator.platform,
          browser: navigator.appName || 'Unknown',
          userAgent: navigator.userAgent,
        };

        // Create the session
        const response = await registerSession({deviceInfo}).unwrap();

        // Save session ID
        const newSessionId = response._id;
        setSessionId(newSessionId);
        setMaxSessionsReached(false);

        // Start heartbeat to keep session alive
        console.log('🚀 ~ createNewSession ~ heartBeat:');
        await heartBeat();

        return newSessionId;
      } catch (error: any) {
        // Check if it's a session limit error (409 Conflict)
        if ((error as ApiError)?.status === 409) {
          setMaxSessionsReached(true);
          setIsSessionAlertOpen(true);
          // toast.error(
          //   t(
          //     'Maximum session limit reached. Please disconnect from another device.',
          //   ),
          // );
        }
        console.error('Error creating new session:', error);
        return null;
      } finally {
        activeCreateSessionPromiseRef.current = null;
      }
    })();

    // Store the promise for reuse
    activeCreateSessionPromiseRef.current = createPromise;

    return createPromise;
  });

  // Revoke a specific session
  const revokeSession = useEvent(
    async (targetSessionId: string, isLogout?: boolean): Promise<boolean> => {
      try {
        await revokeSessionMutation(targetSessionId);
        // refresh firebase token
        await auth.currentUser?.getIdToken(true);
        // If current session is revoked, clean up
        if (targetSessionId === sessionId) {
          await cleanupSession();

          // Force logout if current session is revoked and not already logging out
          if (!isLogout) {
            await logout();
          }
        } else {
          // Refresh session list
          await refreshSessions();
        }

        return true;
      } catch (error) {
        console.error('Error revoking session:', error);
        return false;
      }
    },
  );

  // Revoke all other sessions except current
  const revokeOtherSessions = useEvent(async (): Promise<boolean> => {
    if (!sessionId) return false;

    try {
      await revokeAllSessionsMutation({
        exceptSessionId: sessionId,
      }).unwrap();

      // Refresh session list
      await refreshSessions();

      return true;
    } catch (error) {
      console.error('Error revoking other sessions:', error);
      return false;
    }
  });

  useEffect(() => {
    const handleAppFocus = async () => {
      console.log('🚀 ~ handleAppFocus ~ heartBeat:');
      await heartBeat();
    };

    // Refresh token when app comes into focus
    window.addEventListener('focus', handleAppFocus);

    return () => {
      window.removeEventListener('focus', handleAppFocus);
      // Clean up any pending heartbeat request when unmounting
      if (heartbeatAbortControllerRef.current) {
        heartbeatAbortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <SessionContext.Provider
        value={{
          sessionId,
          userSessions,
          isLoading: isLoading || isLoadingSessions,
          maxSessions,
          maxSessionsReached,
          isSessionAlertOpen,
          createNewSession,
          revokeSession,
          revokeOtherSessions,
          refreshSessions: async () => {
            await refreshSessions();
          },
          initializeSession,
          handleExpiredSession,
          heartBeat,
          setIsSessionAlertOpen,
        }}>
        {children}
      </SessionContext.Provider>
    </>
  );
};
